import React, { useState, useEffect, useMemo } from 'react';
import {
  TagIcon, PlusIcon, PencilSquareIcon, TrashIcon,
  ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon,
  ArrowsPointingOutIcon, ArrowsPointingInIcon, FolderIcon,
  FolderOpenIcon, ArrowPathIcon, SparklesIcon,
  Squares2X2Icon, ListBulletIcon, CheckCircleIcon, XCircleIcon
} from '@heroicons/react/24/outline';
import { useQuery, useMutation } from '../../hooks';
import { categoriesAPI } from '../../services/api';
import {
  Card, SectionHeader, Button, Badge, StatusBadge, Modal, Input,
  LoadingPage, EmptyState,
} from '../../components/common/UI';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

// ── Base URL & Image URL Resolver ────────────────────────
const BASE_URL = (process.env.REACT_APP_BASE_URL || 'http://localhost:5000').replace(/\/$/, '');

export const resolveImageUrl = (pathOrUrl) => {
  if (!pathOrUrl || typeof pathOrUrl !== 'string') return '';
  const trimmed = pathOrUrl.trim();
  if (!trimmed) return '';
  if (/^(https?:|\/\/|blob:|data:)/i.test(trimmed)) {
    return trimmed;
  }
  const normalized = trimmed.replace(/\\/g, '/');
  const cleanPath = normalized.startsWith('/') ? normalized : `/${normalized}`;
  return `${BASE_URL}${cleanPath}`;
};

export const getCategoryImageUrl = (cat) => {
  if (!cat || typeof cat !== 'object') return '';
  const raw =
    cat.icon_url ||
    cat.icon ||
    cat.iconUrl ||
    cat.image_url ||
    cat.image ||
    cat.imageUrl ||
    cat.thumbnail ||
    cat.banner_url ||
    cat.banner ||
    cat.photo ||
    cat.img ||
    cat.image_path ||
    '';
  return resolveImageUrl(raw);
};

// ── Category Thumbnail / Icon Component with Fallback ─────
export const CategoryThumbnail = ({
  cat,
  level = 0,
  hasChildren = false,
  isExpanded = false,
  size = 'md',
  className = '',
}) => {
  const [hasError, setHasError] = useState(false);
  const imageUrl = getCategoryImageUrl(cat);

  const sizeClasses =
    size === 'sm'
      ? 'h-7 w-7'
      : size === 'lg'
      ? 'h-10 w-10'
      : level === 0
      ? 'h-9 w-9'
      : 'h-8 w-8';

  const iconSize =
    size === 'sm'
      ? 'h-3.5 w-3.5'
      : size === 'lg'
      ? 'h-5 w-5'
      : 'h-4 w-4';

  if (imageUrl && !hasError) {
    return (
      <img
        src={imageUrl}
        alt={cat?.name || 'Category'}
        onError={() => setHasError(true)}
        className={`rounded-lg object-cover border border-surface-200 bg-white shadow-xs ${sizeClasses} ${className}`}
      />
    );
  }

  return (
    <div
      className={`rounded-lg flex items-center justify-center border border-surface-200 bg-white shadow-xs ${sizeClasses} ${
        level === 0 ? 'text-brand-600' : 'text-surface-400'
      } ${className}`}
    >
      {hasChildren ? (
        isExpanded ? (
          <FolderOpenIcon className={`${iconSize} text-brand-500`} />
        ) : (
          <FolderIcon className={`${iconSize} text-brand-500`} />
        )
      ) : (
        <TagIcon className={iconSize} />
      )}
    </div>
  );
};

// ── Helper: Normalize any backend response (flat, tree, rows, data) ──
const normalizeCategories = (raw) => {
  const flat = [];
  const visited = new Set();

  const extract = (item, inheritedParentId = null) => {
    if (!item || typeof item !== 'object') return;
    
    // Unify ID
    const rawId = item.id ?? item._id ?? item.category_id;
    if (rawId === undefined || rawId === null) return;
    
    const strId = String(rawId);
    if (visited.has(strId)) return;
    visited.add(strId);

    // Unify parent_id
    const rawPid =
      item.parent_id ??
      item.parentId ??
      item.parent_category_id ??
      (item.parent && typeof item.parent === 'object' ? (item.parent.id ?? item.parent._id) : item.parent) ??
      item.parent_slug ??
      item.parent_name ??
      inheritedParentId;

    const isRoot =
      rawPid === null ||
      rawPid === undefined ||
      rawPid === '' ||
      rawPid === 0 ||
      rawPid === '0' ||
      rawPid === 'null' ||
      rawPid === 'undefined';

    const finalParentId = !isRoot ? rawPid : null;

    // Extract child arrays if present in the object
    const childList =
      item.children ||
      item.subcategories ||
      item.sub_categories ||
      item.subCategories ||
      item.child_categories ||
      item.childCategories ||
      item.sub_sub_categories;

    const {
      children,
      subcategories,
      sub_categories,
      subCategories,
      child_categories,
      childCategories,
      sub_sub_categories,
      ...cleanItem
    } = item;

    const imgVal =
      cleanItem.image_url ||
      cleanItem.image ||
      cleanItem.imageUrl ||
      cleanItem.thumbnail ||
      cleanItem.banner_url ||
      cleanItem.banner ||
      cleanItem.photo ||
      cleanItem.img ||
      cleanItem.image_path ||
      '';

    const iconVal =
      cleanItem.icon_url ||
      cleanItem.icon ||
      cleanItem.iconUrl ||
      cleanItem.icon_path ||
      '';

    flat.push({
      ...cleanItem,
      id: rawId,
      parent_id: finalParentId,
      image_url: imgVal,
      icon_url: iconVal,
    });

    if (Array.isArray(childList)) {
      childList.forEach((child) => {
        extract(child, rawId);
      });
    }
  };

  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.rows)
    ? raw.rows
    : Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw?.categories)
    ? raw.categories
    : Array.isArray(raw?.items)
    ? raw.items
    : [];

  list.forEach((item) => extract(item, null));
  return flat;
};

// ── Helper: Build Category Hierarchy Tree ────────────────
const buildCategoryTree = (categories = []) => {
  const mapById = {};
  const mapBySlug = {};
  const mapByName = {};
  const roots = [];

  // 1. Initialize maps
  categories.forEach((cat) => {
    const strId = String(cat.id);
    const node = { ...cat, children: [] };
    mapById[strId] = node;
    if (cat.slug) mapBySlug[String(cat.slug).toLowerCase().trim()] = node;
    if (cat.name) mapByName[String(cat.name).toLowerCase().trim()] = node;
  });

  // 2. Build parent-child relationships with multi-strategy lookup (ID, slug, name)
  categories.forEach((cat) => {
    const rawPid = cat.parent_id;
    const strId = String(cat.id);
    const node = mapById[strId];

    const isRoot =
      rawPid === null ||
      rawPid === undefined ||
      rawPid === '' ||
      rawPid === 0 ||
      rawPid === '0' ||
      rawPid === 'null' ||
      rawPid === 'undefined';

    if (!isRoot) {
      const pidStr = String(rawPid);
      const pidLower = pidStr.toLowerCase().trim();

      // Lookup parent by ID, Slug, or Name
      const parentNode =
        mapById[pidStr] ||
        mapBySlug[pidLower] ||
        mapByName[pidLower];

      if (parentNode && String(parentNode.id) !== strId) {
        // Avoid duplicate insertion
        if (!parentNode.children.some((c) => String(c.id) === strId)) {
          parentNode.children.push(node);
        }
        return;
      }
    }

    // Otherwise it's a top-level root category
    roots.push(node);
  });

  // 3. Sort recursively by sort_order ascending, then by name
  const sortTree = (nodes) => {
    nodes.sort((a, b) => {
      const orderA = Number(a.sort_order) || 0;
      const orderB = Number(b.sort_order) || 0;
      if (orderA !== orderB) return orderA - orderB;
      return (a.name || '').localeCompare(b.name || '');
    });
    nodes.forEach((n) => sortTree(n.children));
  };

  sortTree(roots);
  return { roots, map: mapById };
};

// ── Helper: Get all descendant IDs of a category ──────────
const getDescendantIds = (catId, map) => {
  const descendants = new Set();
  const traverse = (id) => {
    const node = map[String(id)];
    if (node && node.children) {
      node.children.forEach((child) => {
        descendants.add(String(child.id));
        traverse(child.id);
      });
    }
  };
  traverse(catId);
  return descendants;
};

// ── Helper: Format slug helper ───────────────────────────
const slugify = (text = '') =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

// ── Category Modal (Create & Edit at Any Hierarchy Level) ─
const CategoryModal = ({
  cat,
  defaultParentId = '',
  open,
  onClose,
  onSaved,
  categories = [],
  business,
}) => {
  const isEdit = !!cat;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      slug: '',
      parent_id: '',
      description: '',
      image_url: '',
      icon_url: '',
      sort_order: 0,
      gender: 'all',
      is_gender_applicable: 0,
      is_active: 1,
      meta_title: '',
      meta_description: '',
    },
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[String(c.id)] = c;
    });
    return map;
  }, [categories]);

  // Prevent circular parent selection
  const disabledParentIds = useMemo(() => {
    if (!cat) return new Set();
    const tree = buildCategoryTree(categories);
    const descendants = getDescendantIds(cat.id, tree.map);
    descendants.add(String(cat.id));
    return descendants;
  }, [cat, categories]);

  useEffect(() => {
    if (open) {
      if (cat) {
        reset({
          name: cat.name || '',
          slug: cat.slug || '',
          parent_id:
            cat.parent_id !== null && cat.parent_id !== undefined && cat.parent_id !== 0 && cat.parent_id !== '0'
              ? String(cat.parent_id)
              : '',
          description: cat.description || '',
          image_url: cat.image_url || cat.image || cat.thumbnail || cat.banner_url || '',
          icon_url: cat.icon_url || cat.icon || '',
          sort_order: cat.sort_order ?? 0,
          gender: cat.gender || 'all',
          is_gender_applicable: cat.is_gender_applicable ? 1 : 0,
          is_active: cat.is_active !== undefined ? (cat.is_active ? 1 : 0) : 1,
          meta_title: cat.meta_title || '',
          meta_description: cat.meta_description || '',
        });
      } else {
        reset({
          name: '',
          slug: '',
          parent_id: defaultParentId ? String(defaultParentId) : '',
          description: '',
          image_url: '',
          icon_url: '',
          sort_order: 0,
          gender: 'all',
          is_gender_applicable: 0,
          is_active: 1,
          meta_title: '',
          meta_description: '',
        });
      }
      setImageFile(null);
      setImagePreview(null);
      setIconFile(null);
      setIconPreview(null);
    }
  }, [open, cat, defaultParentId, reset]);

  const { mutate: save, loading } = useMutation(
    async (formDataValues) => {
      const d = { ...formDataValues };
      d.parent_id = d.parent_id ? Number(d.parent_id) : null;
      d.sort_order = Number(d.sort_order) || 0;
      d.is_active = d.is_active ? 1 : 0;
      d.is_gender_applicable = d.is_gender_applicable ? 1 : 0;

      let payload;
      if (imageFile || iconFile) {
        payload = new FormData();
        Object.keys(d).forEach((k) => {
          if (['children_count', 'children', 'parent', 'created_at', 'updated_at'].includes(k)) return;
          if (d[k] !== null && d[k] !== undefined) payload.append(k, d[k]);
        });
        if (imageFile) payload.append('image', imageFile);
        if (iconFile) payload.append('icon', iconFile);
        if (!isEdit && business?.id) payload.append('business_id', business.id);
      } else {
        const { children_count, children, parent, created_at, updated_at, ...clean } = d;
        payload = isEdit ? clean : { ...clean, business_id: business?.id };
      }

      return isEdit ? categoriesAPI.update(cat.id, payload) : categoriesAPI.create(payload);
    },
    {
      successMsg: isEdit ? 'Category updated successfully' : 'Category created successfully',
      onSuccess: () => {
        onSaved?.();
      },
    }
  );

  // Render parent category dropdown options with nested tree indentation
  const parentOptions = useMemo(() => {
    const { roots } = buildCategoryTree(categories);
    const options = [];

    const traverse = (node, depth) => {
      const isDisabled = disabledParentIds.has(String(node.id));
      const indent = '\u00A0\u00A0\u00A0\u00A0'.repeat(depth);
      const prefix = depth === 0 ? '📁 ' : depth === 1 ? '↳ 📂 ' : '↳ 🏷️ ';
      const levelLabel = depth === 0 ? ' (Root)' : depth === 1 ? ' (Sub)' : ` (Sub-sub / L${depth})`;

      options.push(
        <option key={node.id} value={node.id} disabled={isDisabled}>
          {indent}{prefix}{node.name}{isDisabled ? ' (Current / Child)' : levelLabel}
        </option>
      );

      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => traverse(child, depth + 1));
      }
    };

    roots.forEach((root) => traverse(root, 0));
    return options;
  }, [categories, disabledParentIds]);

  const watchedName = watch('name');
  const watchedParentId = watch('parent_id');
  const watchedImageUrl = watch('image_url');
  const watchedIconUrl = watch('icon_url');
  const parentCategory = watchedParentId ? categoryMap[String(watchedParentId)] : null;

  const bannerPreviewSrc =
    imagePreview ||
    resolveImageUrl(watchedImageUrl || cat?.image_url || cat?.image || cat?.thumbnail || cat?.banner_url);
  const iconPreviewSrc =
    iconPreview ||
    resolveImageUrl(watchedIconUrl || cat?.icon_url || cat?.icon);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-brand-50 text-brand-600">
            {isEdit ? <PencilSquareIcon className="h-5 w-5" /> : <PlusIcon className="h-5 w-5" />}
          </div>
          <div>
            <h3 className="text-base font-semibold text-surface-900">
              {isEdit ? `Edit Category: ${cat.name}` : 'Create New Category'}
            </h3>
            <p className="text-xs text-surface-500 font-normal">
              {parentCategory
                ? `Adding under "${parentCategory.name}"`
                : 'Top-level root category'}
            </p>
          </div>
        </div>
      }
      size="xl"
    >
      <div className="max-h-[72vh] overflow-y-auto pr-1 scrollbar-thin">
        <form onSubmit={handleSubmit(save)} className="space-y-6 py-1">
          {/* ── Basic Information ── */}
          <div className="space-y-4 rounded-xl bg-surface-50/60 p-4 border border-surface-200/60">
            <h3 className="text-xs font-bold uppercase tracking-wider text-surface-500 flex items-center gap-1.5">
              <TagIcon className="h-4 w-4 text-brand-600" /> Basic Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Category Name *"
                  placeholder="e.g. Men's Wear, Electronics, Shoes..."
                  error={errors.name?.message}
                  {...register('name', { required: 'Name is required' })}
                  onChange={(e) => {
                    register('name').onChange(e);
                    if (!isEdit) {
                      setValue('slug', slugify(e.target.value));
                    }
                  }}
                />
              </div>

              <div>
                <Input
                  label="Slug"
                  placeholder="e.g. mens-wear"
                  hint={
                    <span className="flex items-center justify-between">
                      <span>URL path: /{watch('slug') || '...'}</span>
                      {watchedName && (
                        <button
                          type="button"
                          className="text-brand-600 hover:underline text-[11px] font-medium"
                          onClick={() => setValue('slug', slugify(watchedName))}
                        >
                          Auto-generate
                        </button>
                      )}
                    </span>
                  }
                  {...register('slug')}
                />
              </div>
            </div>

            <div>
              <label className="label flex items-center justify-between">
                <span>Parent Category (Hierarchy Level)</span>
                {parentCategory && (
                  <span className="text-xs font-normal text-brand-600">
                    Child of: <strong>{parentCategory.name}</strong>
                  </span>
                )}
              </label>
              <select className="input cursor-pointer" {...register('parent_id')}>
                <option value="">🌳 — Top-level Root Category (No Parent) —</option>
                {parentOptions}
              </select>
              <p className="text-[11px] text-surface-400 mt-1">
                Select a parent category to make this a subcategory or sub-sub category.
              </p>
            </div>

            <div>
              <label className="label">Description</label>
              <textarea
                className="input resize-none"
                rows={2}
                placeholder="Brief summary of items in this category..."
                {...register('description')}
              />
            </div>
          </div>

          {/* ── Media & Visuals ── */}
          <div className="space-y-4 rounded-xl bg-surface-50/60 p-4 border border-surface-200/60">
            <h3 className="text-xs font-bold uppercase tracking-wider text-surface-500 flex items-center gap-1.5">
              <FolderIcon className="h-4 w-4 text-brand-600" /> Images & Icons
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Banner / Image */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-surface-700">
                  Category Banner / Thumbnail
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="text-xs block w-full file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setImageFile(file);
                    setImagePreview(URL.createObjectURL(file));
                  }}
                />
                <Input
                  type="url"
                  placeholder="Or enter image URL (https://...)"
                  {...register('image_url')}
                />
                {bannerPreviewSrc && (
                  <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-white border border-surface-200">
                    <img
                      src={bannerPreviewSrc}
                      alt="Banner Preview"
                      className="h-12 w-20 object-cover rounded-md border"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <div className="text-xs">
                      <p className="font-medium text-surface-700">Banner Preview</p>
                      <p className="text-surface-400 text-[10px]">
                        {imageFile ? imageFile.name : 'Current Image'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Nav / Icon */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-surface-700">
                  Nav / Sidebar Icon
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="text-xs block w-full file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setIconFile(file);
                    setIconPreview(URL.createObjectURL(file));
                  }}
                />
                <Input
                  type="url"
                  placeholder="Or enter icon URL (https://...)"
                  {...register('icon_url')}
                />
                {iconPreviewSrc && (
                  <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-white border border-surface-200">
                    <img
                      src={iconPreviewSrc}
                      alt="Icon Preview"
                      className="h-10 w-10 object-contain rounded-md border p-1 bg-surface-50"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <div className="text-xs">
                      <p className="font-medium text-surface-700">Icon Preview</p>
                      <p className="text-surface-400 text-[10px]">
                        {iconFile ? iconFile.name : 'Current Icon'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Display, Status & Attributes ── */}
          <div className="space-y-4 rounded-xl bg-surface-50/60 p-4 border border-surface-200/60">
            <h3 className="text-xs font-bold uppercase tracking-wider text-surface-500 flex items-center gap-1.5">
              <SparklesIcon className="h-4 w-4 text-brand-600" /> Display & Targeting
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Sort Order"
                type="number"
                hint="Lower number appears first (0, 1, 2...)"
                {...register('sort_order')}
              />

              <div>
                <label className="label">Target Gender</label>
                <select className="input cursor-pointer" {...register('gender')}>
                  <option value="all">All Genders</option>
                  <option value="men">Men</option>
                  <option value="women">Women</option>
                  <option value="kids">Kids</option>
                  <option value="unisex">Unisex</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <label className="flex items-center gap-3 p-3 rounded-xl bg-white border border-surface-200 hover:border-brand-400 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-brand-600 rounded border-surface-300 focus:ring-brand-500"
                  {...register('is_active')}
                />
                <div>
                  <p className="text-sm font-medium text-surface-900">Active Category</p>
                  <p className="text-xs text-surface-400">Visible to customers in store</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-xl bg-white border border-surface-200 hover:border-brand-400 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-brand-600 rounded border-surface-300 focus:ring-brand-500"
                  {...register('is_gender_applicable')}
                />
                <div>
                  <p className="text-sm font-medium text-surface-900">Gender Applicable</p>
                  <p className="text-xs text-surface-400">Prompt gender filter on products</p>
                </div>
              </label>
            </div>
          </div>

          {/* ── SEO Settings ── */}
          <div className="space-y-4 rounded-xl bg-surface-50/60 p-4 border border-surface-200/60">
            <h3 className="text-xs font-bold uppercase tracking-wider text-surface-500">
              SEO & Search Optimization
            </h3>

            <Input
              label="Meta Title"
              placeholder="e.g. Best Men's T-Shirts Online | Shop Now"
              {...register('meta_title')}
            />

            <div>
              <label className="label">Meta Description</label>
              <textarea
                className="input resize-none"
                rows={2}
                placeholder="Meta description for search engine results..."
                {...register('meta_description')}
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-surface-200">
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={loading} icon={isEdit ? PencilSquareIcon : PlusIcon}>
              {isEdit ? 'Save Changes' : 'Create Category'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

// ── Interactive Recursive Category Row ───────────────────
const CategoryTreeNode = ({
  node,
  level = 0,
  expandedIds,
  toggleExpand,
  onEdit,
  onAddSub,
  onToggleStatus,
  onDelete,
  allCategoriesMap,
  searchQuery = '',
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.has(String(node.id));
  const isSearchActive = !!searchQuery.trim();

  // Highlight matching text if searching
  const isMatched = searchQuery
    ? (node.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (node.slug || '').toLowerCase().includes(searchQuery.toLowerCase())
    : false;

  // Level badges
  const levelBadge = () => {
    if (level === 0) {
      return (
        <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-purple-100 text-purple-700 border border-purple-200">
          Root Category
        </span>
      );
    }
    if (level === 1) {
      return (
        <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-blue-100 text-blue-700 border border-blue-200">
          Subcategory
        </span>
      );
    }
    if (level === 2) {
      return (
        <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-amber-100 text-amber-800 border border-amber-200">
          Sub-sub Category
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-surface-100 text-surface-600 border border-surface-200">
        Level {level}
      </span>
    );
  };

  const rawPid = node.parent_id;
  const isRootNode =
    rawPid === null ||
    rawPid === undefined ||
    rawPid === '' ||
    rawPid === 0 ||
    rawPid === '0' ||
    rawPid === 'null' ||
    rawPid === 'undefined';

  const parentName = !isRootNode ? allCategoriesMap[String(rawPid)]?.name : null;

  const handleRowClick = () => {
    if (hasChildren) {
      toggleExpand(node.id);
    }
  };

  return (
    <React.Fragment>
      <tr
        onClick={handleRowClick}
        className={`group border-b border-surface-100 transition-colors duration-150 cursor-pointer ${
          level === 0
            ? 'bg-white hover:bg-surface-50 font-medium'
            : level === 1
            ? 'bg-surface-50/70 hover:bg-brand-50/40'
            : 'bg-surface-100/60 hover:bg-brand-50/60'
        } ${isMatched ? 'bg-yellow-50/80 ring-1 ring-yellow-300' : ''}`}
      >
        {/* Hierarchy Tree Column */}
        <td className="py-3 px-4">
          <div className="flex items-center gap-2.5" style={{ paddingLeft: `${level * 28}px` }}>
            {/* Visual tree guide line connector */}
            {level > 0 && (
              <span className="text-surface-300 select-none font-mono text-xs flex-shrink-0">
                {level === 1 ? '├──' : '└──'}
              </span>
            )}

            {/* Expand / Collapse Button */}
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(node.id);
                }}
                className={`p-1.5 rounded-lg transition-all flex-shrink-0 shadow-sm ${
                  isExpanded
                    ? 'bg-brand-600 text-white rotate-90 ring-2 ring-brand-200'
                    : 'bg-surface-100 text-surface-700 hover:bg-brand-50 hover:text-brand-700'
                }`}
                title={isExpanded ? 'Collapse Subcategories' : 'Click to show subcategories'}
              >
                <ChevronRightIcon className="h-4 w-4 transition-transform duration-200 stroke-[2.5]" />
              </button>
            ) : (
              <div className="w-7 flex items-center justify-center flex-shrink-0">
                <div
                  className={`w-2 h-2 rounded-full ${
                    level === 0 ? 'bg-purple-400' : level === 1 ? 'bg-blue-400' : 'bg-amber-400'
                  }`}
                />
              </div>
            )}

            {/* Thumbnail / Icon */}
            <div className="relative flex-shrink-0">
              <CategoryThumbnail
                cat={node}
                level={level}
                hasChildren={hasChildren}
                isExpanded={isExpanded}
                size={level === 0 ? 'md' : 'sm'}
              />
            </div>

            {/* Name + Details */}
            <div className="min-w-0 select-none">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`font-medium ${
                    level === 0
                      ? 'text-surface-900 text-sm font-semibold'
                      : level === 1
                      ? 'text-surface-800 text-sm font-medium'
                      : 'text-surface-700 text-xs'
                  }`}
                >
                  {node.name}
                </span>

                {levelBadge()}

                {/* Subcategories count badge - click to expand */}
                {hasChildren && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(node.id);
                    }}
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-all ${
                      isExpanded
                        ? 'bg-brand-600 text-white shadow-sm'
                        : 'bg-brand-50 text-brand-700 border border-brand-200 hover:bg-brand-100'
                    }`}
                  >
                    <span>{node.children.length}</span>
                    <span>
                      {level === 0 ? 'subcategories' : 'sub-sub'}
                    </span>
                    <span className="text-[10px]">{isExpanded ? '▲' : '▼ (click to open)'}</span>
                  </button>
                )}
              </div>

              {node.description && (
                <p className="text-[11px] text-surface-400 truncate max-w-sm mt-0.5">
                  {node.description}
                </p>
              )}
            </div>
          </div>
        </td>

        {/* Slug */}
        <td className="py-3 px-3">
          <span className="font-mono text-xs text-surface-500 bg-surface-100 px-2 py-1 rounded-md">
            /{node.slug}
          </span>
        </td>

        {/* Parent Category Info */}
        <td className="py-3 px-3 text-xs">
          {parentName ? (
            <span className="inline-flex items-center gap-1 text-surface-600 bg-surface-100 px-2 py-1 rounded-md">
              <span className="text-surface-400">↳</span> {parentName}
            </span>
          ) : (
            <span className="text-purple-600 font-medium bg-purple-50 px-2 py-1 rounded-md text-[11px]">
              👑 Root Category
            </span>
          )}
        </td>

        {/* Gender */}
        <td className="py-3 px-3 text-xs">
          {node.gender && node.gender !== 'all' ? (
            <span className="capitalize px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-medium text-[11px]">
              {node.gender}
            </span>
          ) : (
            <span className="text-surface-400 text-[11px]">All</span>
          )}
        </td>

        {/* Sort Order */}
        <td className="py-3 px-3 font-mono text-xs text-surface-500">
          {node.sort_order ?? 0}
        </td>

        {/* Status Toggle */}
        <td className="py-3 px-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleStatus(node);
            }}
            className="group/toggle inline-flex items-center gap-1.5 focus:outline-none"
            title="Click to toggle status"
          >
            <StatusBadge status={node.is_active ? 'active' : 'suspended'} />
          </button>
        </td>

        {/* Actions (Full Editability at Every Level!) */}
        <td className="py-3 px-4 text-right">
          <div className="flex items-center justify-end gap-1.5 opacity-90 group-hover:opacity-100">
            {/* Add Child Subcategory button */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              icon={PlusIcon}
              className="text-xs py-1 px-2 h-7 font-medium text-brand-600 border-brand-200 hover:bg-brand-50"
              onClick={(e) => {
                e.stopPropagation();
                onAddSub(node);
              }}
              title={`Add child category under "${node.name}"`}
            >
              + Sub
            </Button>

            {/* Edit Category Button */}
            <button
              type="button"
              className="p-1.5 rounded-lg text-surface-500 hover:text-brand-600 hover:bg-brand-50 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(node);
              }}
              title="Edit this category"
            >
              <PencilSquareIcon className="h-4 w-4" />
            </button>

            {/* Delete Category Button */}
            <button
              type="button"
              className="p-1.5 rounded-lg text-surface-400 hover:text-danger-600 hover:bg-danger-50 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(node);
              }}
              title="Delete this category"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>

      {/* Render children subcategories if expanded (or searching) */}
      {hasChildren && (isExpanded || isSearchActive) && (
        node.children.map((child) => (
          <CategoryTreeNode
            key={child.id}
            node={child}
            level={level + 1}
            expandedIds={expandedIds}
            toggleExpand={toggleExpand}
            onEdit={onEdit}
            onAddSub={onAddSub}
            onToggleStatus={onToggleStatus}
            onDelete={onDelete}
            allCategoriesMap={allCategoriesMap}
            searchQuery={searchQuery}
          />
        ))
      )}
    </React.Fragment>
  );
};

// ── Interactive Drilldown Column Explorer ─────────────────
const CategoryDrilldownExplorer = ({
  categories = [],
  onEdit,
  onAddSub,
  onToggleStatus,
  onDelete,
}) => {
  const { roots, map } = useMemo(() => buildCategoryTree(categories), [categories]);
  const [selectedRootId, setSelectedRootId] = useState(null);
  const [selectedSubId, setSelectedSubId] = useState(null);

  // Set default selected root on load
  useEffect(() => {
    if (roots.length > 0 && !selectedRootId) {
      setSelectedRootId(roots[0].id);
    }
  }, [roots, selectedRootId]);

  const selectedRoot = selectedRootId ? map[String(selectedRootId)] : null;
  const subCategories = selectedRoot?.children || [];

  const selectedSub = selectedSubId ? map[String(selectedSubId)] : null;
  const subSubCategories = selectedSub?.children || [];

  return (
    <div className="space-y-4">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-2 p-3 rounded-xl bg-surface-100/70 border border-surface-200 text-sm overflow-x-auto">
        <span className="font-semibold text-surface-500">Path:</span>
        <button
          onClick={() => {
            setSelectedSubId(null);
          }}
          className="text-brand-600 font-medium hover:underline flex items-center gap-1"
        >
          <span>Root Categories</span>
        </button>
        {selectedRoot && (
          <>
            <span className="text-surface-400">/</span>
            <span className="font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
              {selectedRoot.name}
            </span>
          </>
        )}
        {selectedSub && (
          <>
            <span className="text-surface-400">/</span>
            <span className="font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
              {selectedSub.name}
            </span>
          </>
        )}
      </div>

      {/* 3-Column Drilldown Columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[480px]">
        {/* Column 1: Root Categories */}
        <Card className="flex flex-col p-0 overflow-hidden border-purple-200 shadow-sm">
          <div className="p-3 bg-purple-50/80 border-b border-purple-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-600" />
              <h4 className="font-semibold text-xs uppercase tracking-wider text-purple-900">
                1. Root Categories ({roots.length})
              </h4>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-surface-100 p-1">
            {roots.length === 0 ? (
              <p className="p-4 text-center text-xs text-surface-400">No root categories</p>
            ) : (
              roots.map((root) => {
                const isSelected = String(selectedRootId) === String(root.id);
                return (
                  <div
                    key={root.id}
                    onClick={() => {
                      setSelectedRootId(root.id);
                      setSelectedSubId(null);
                    }}
                    className={`p-3 rounded-lg cursor-pointer transition-all flex items-center justify-between group ${
                      isSelected
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'hover:bg-purple-50/60 text-surface-800'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <CategoryThumbnail
                        cat={root}
                        level={0}
                        hasChildren={root.children?.length > 0}
                        size="md"
                      />
                      <div className="min-w-0">
                        <p className={`font-medium text-sm truncate ${isSelected ? 'text-white' : ''}`}>
                          {root.name}
                        </p>
                        <p
                          className={`text-[11px] truncate ${
                            isSelected ? 'text-purple-200' : 'text-surface-400'
                          }`}
                        >
                          /{root.slug} • {root.children.length} sub-items
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(root);
                        }}
                        className={`p-1 rounded hover:bg-black/10 transition-colors ${
                          isSelected ? 'text-white' : 'text-surface-400 hover:text-surface-900'
                        }`}
                        title="Edit"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <ChevronRightIcon
                        className={`h-4 w-4 ${
                          isSelected ? 'text-white' : 'text-surface-300'
                        }`}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* Column 2: Subcategories (Level 1) */}
        <Card className="flex flex-col p-0 overflow-hidden border-blue-200 shadow-sm">
          <div className="p-3 bg-blue-50/80 border-b border-blue-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />
              <h4 className="font-semibold text-xs uppercase tracking-wider text-blue-900 truncate">
                2. Subcategories ({subCategories.length})
              </h4>
            </div>
            {selectedRoot && (
              <Button
                variant="outline"
                size="sm"
                icon={PlusIcon}
                className="text-[11px] h-6 px-2 py-0 border-blue-300 text-blue-700 bg-white"
                onClick={() => onAddSub(selectedRoot)}
              >
                + Sub
              </Button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-surface-100 p-1">
            {!selectedRoot ? (
              <p className="p-8 text-center text-xs text-surface-400">Select a root category first</p>
            ) : subCategories.length === 0 ? (
              <div className="p-8 text-center text-xs text-surface-400 space-y-2">
                <p>No subcategories under "{selectedRoot.name}"</p>
                <Button
                  variant="primary"
                  size="sm"
                  icon={PlusIcon}
                  onClick={() => onAddSub(selectedRoot)}
                >
                  Add Subcategory
                </Button>
              </div>
            ) : (
              subCategories.map((sub) => {
                const isSelected = String(selectedSubId) === String(sub.id);
                return (
                  <div
                    key={sub.id}
                    onClick={() => setSelectedSubId(sub.id)}
                    className={`p-3 rounded-lg cursor-pointer transition-all flex items-center justify-between group ${
                      isSelected
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'hover:bg-blue-50/60 text-surface-800'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <CategoryThumbnail
                        cat={sub}
                        level={1}
                        hasChildren={sub.children?.length > 0}
                        size="md"
                      />
                      <div className="min-w-0">
                        <p className={`font-medium text-sm truncate ${isSelected ? 'text-white' : ''}`}>
                          {sub.name}
                        </p>
                        <p
                          className={`text-[11px] truncate ${
                            isSelected ? 'text-blue-200' : 'text-surface-400'
                          }`}
                        >
                          /{sub.slug} • {sub.children.length} sub-sub items
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(sub);
                        }}
                        className={`p-1 rounded hover:bg-black/10 transition-colors ${
                          isSelected ? 'text-white' : 'text-surface-400 hover:text-surface-900'
                        }`}
                        title="Edit"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <ChevronRightIcon
                        className={`h-4 w-4 ${
                          isSelected ? 'text-white' : 'text-surface-300'
                        }`}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* Column 3: Sub-sub Categories (Level 2) */}
        <Card className="flex flex-col p-0 overflow-hidden border-amber-200 shadow-sm">
          <div className="p-3 bg-amber-50/80 border-b border-amber-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-600" />
              <h4 className="font-semibold text-xs uppercase tracking-wider text-amber-900 truncate">
                3. Sub-sub Categories ({subSubCategories.length})
              </h4>
            </div>
            {selectedSub && (
              <Button
                variant="outline"
                size="sm"
                icon={PlusIcon}
                className="text-[11px] h-6 px-2 py-0 border-amber-300 text-amber-800 bg-white"
                onClick={() => onAddSub(selectedSub)}
              >
                + Sub-sub
              </Button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-surface-100 p-1">
            {!selectedSub ? (
              <p className="p-8 text-center text-xs text-surface-400">
                Select a subcategory to view its sub-sub categories
              </p>
            ) : subSubCategories.length === 0 ? (
              <div className="p-8 text-center text-xs text-surface-400 space-y-2">
                <p>No sub-sub categories under "{selectedSub.name}"</p>
                <Button
                  variant="primary"
                  size="sm"
                  icon={PlusIcon}
                  onClick={() => onAddSub(selectedSub)}
                >
                  Add Sub-sub Category
                </Button>
              </div>
            ) : (
              subSubCategories.map((subsub) => (
                <div
                  key={subsub.id}
                  className="p-3 rounded-lg hover:bg-amber-50/60 text-surface-800 flex items-center justify-between group transition-colors"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <CategoryThumbnail
                      cat={subsub}
                      level={2}
                      hasChildren={subsub.children?.length > 0}
                      size="md"
                    />
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-surface-900 truncate">{subsub.name}</p>
                      <p className="text-[11px] text-surface-400 truncate">
                        /{subsub.slug} {subsub.children.length > 0 ? `• ${subsub.children.length} deeper` : ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => onToggleStatus(subsub)}
                      className="text-xs"
                    >
                      <StatusBadge status={subsub.is_active ? 'active' : 'suspended'} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(subsub)}
                      className="p-1.5 rounded text-surface-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(subsub)}
                      className="p-1.5 rounded text-surface-400 hover:text-danger-600 hover:bg-danger-50 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

// ── Main Business Categories Page Component ──────────────
export default function CategoriesPage({ business }) {
  const [showModal, setShowModal] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [defaultParentId, setDefaultParentId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('tree'); // 'tree' | 'drilldown'
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [deleteConfirmCat, setDeleteConfirmCat] = useState(null);

  // Fetch all categories (without restrictive business_id filter so all global & store categories load)
  const fetchAllCategories = async () => {
    try {
      const res = await categoriesAPI.list({ limit: 2000, all: 'true' });
      return res;
    } catch (err) {
      try {
        const treeRes = await categoriesAPI.tree();
        return treeRes;
      } catch (e) {
        return [];
      }
    }
  };

  const { data, loading, refetch } = useQuery(fetchAllCategories, []);

  const categories = useMemo(() => normalizeCategories(data), [data]);

  // Category mapping
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[String(c.id)] = c;
    });
    return map;
  }, [categories]);

  // Build tree
  const { roots } = useMemo(() => buildCategoryTree(categories), [categories]);

  // Auto-expand all matching ancestor paths when searching
  useEffect(() => {
    if (!searchQuery.trim()) return;
    const q = searchQuery.toLowerCase().trim();
    const newExpanded = new Set(expandedIds);

    const checkAndExpand = (node) => {
      let matchInDescendant = false;
      if (node.children) {
        node.children.forEach((child) => {
          if (checkAndExpand(child)) matchInDescendant = true;
        });
      }
      const selfMatch =
        (node.name || '').toLowerCase().includes(q) || (node.slug || '').toLowerCase().includes(q);

      if (selfMatch || matchInDescendant) {
        newExpanded.add(String(node.id));
        return true;
      }
      return false;
    };

    roots.forEach((root) => checkAndExpand(root));
    setExpandedIds(newExpanded);
  }, [searchQuery, roots]);

  // Expand / Collapse Helpers
  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      const strId = String(id);
      if (next.has(strId)) next.delete(strId);
      else next.add(strId);
      return next;
    });
  };

  const expandAll = () => {
    const allWithChildren = new Set();
    const traverse = (node) => {
      if (node.children && node.children.length > 0) {
        allWithChildren.add(String(node.id));
        node.children.forEach(traverse);
      }
    };
    roots.forEach(traverse);
    setExpandedIds(allWithChildren);
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  // Status toggle mutation
  const { mutate: toggleStatus } = useMutation(
    (cat) => categoriesAPI.update(cat.id, { is_active: cat.is_active ? 0 : 1 }),
    {
      successMsg: 'Category status updated',
      onSuccess: refetch,
    }
  );

  // Delete mutation
  const { mutate: deleteCat, loading: deleting } = useMutation(
    (id) => categoriesAPI.delete(id),
    {
      successMsg: 'Category deleted successfully',
      onSuccess: () => {
        setDeleteConfirmCat(null);
        refetch();
      },
    }
  );

  // Modal Handlers
  const openCreateRootModal = () => {
    setEditCat(null);
    setDefaultParentId('');
    setShowModal(true);
  };

  const openAddSubModal = (parentCat) => {
    setEditCat(null);
    setDefaultParentId(String(parentCat.id));
    setShowModal(true);
  };

  const openEditModal = (cat) => {
    setEditCat(cat);
    setDefaultParentId('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditCat(null);
    setDefaultParentId('');
  };

  const onSaved = () => {
    refetch();
    closeModal();
  };

  // Quick statistics
  const stats = useMemo(() => {
    let subCount = 0;
    let subSubCount = 0;
    let activeCount = 0;

    categories.forEach((c) => {
      if (c.is_active) activeCount++;
      const rawPid = c.parent_id;
      const isRoot =
        rawPid === null ||
        rawPid === undefined ||
        rawPid === '' ||
        rawPid === 0 ||
        rawPid === '0' ||
        rawPid === 'null' ||
        rawPid === 'undefined';

      if (!isRoot) {
        const parent = categoryMap[String(rawPid)];
        const pParentId = parent?.parent_id;
        const isParentRoot =
          pParentId === null ||
          pParentId === undefined ||
          pParentId === '' ||
          pParentId === 0 ||
          pParentId === '0' ||
          pParentId === 'null' ||
          pParentId === 'undefined';

        if (parent && !isParentRoot) subSubCount++;
        else subCount++;
      }
    });

    return {
      total: categories.length,
      roots: roots.length,
      subs: subCount,
      subsubs: subSubCount,
      active: activeCount,
    };
  }, [categories, roots, categoryMap]);

  return (
    <div className="animate-in space-y-6">
      {/* ── Page Header ── */}
      <SectionHeader
        title="Category Management"
        subtitle="Manage hierarchical categories, subcategories, sub-sub categories, and SEO settings"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" icon={ArrowPathIcon} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={PlusIcon}
              onClick={openCreateRootModal}
            >
              + Add Root Category
            </Button>
          </div>
        }
      />

      {/* ── Stats Overview ── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="p-3.5 rounded-xl bg-white border border-surface-200 shadow-sm flex flex-col justify-between">
          <p className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Total Categories</p>
          <p className="text-2xl font-bold text-surface-900 mt-1">{stats.total}</p>
        </div>
        <div className="p-3.5 rounded-xl bg-purple-50/80 border border-purple-200 shadow-sm flex flex-col justify-between">
          <p className="text-xs font-semibold text-purple-700 uppercase tracking-wider">Root Categories</p>
          <p className="text-2xl font-bold text-purple-900 mt-1">{stats.roots}</p>
        </div>
        <div className="p-3.5 rounded-xl bg-blue-50/80 border border-blue-200 shadow-sm flex flex-col justify-between">
          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Subcategories</p>
          <p className="text-2xl font-bold text-blue-900 mt-1">{stats.subs}</p>
        </div>
        <div className="p-3.5 rounded-xl bg-amber-50/80 border border-amber-200 shadow-sm flex flex-col justify-between">
          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Sub-sub Categories</p>
          <p className="text-2xl font-bold text-amber-900 mt-1">{stats.subsubs}</p>
        </div>
        <div className="p-3.5 rounded-xl bg-emerald-50/80 border border-emerald-200 shadow-sm flex flex-col justify-between col-span-2 sm:col-span-1">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Active in Store</p>
          <p className="text-2xl font-bold text-emerald-900 mt-1">{stats.active}</p>
        </div>
      </div>

      {/* ── Search, Filter & View Controls Bar ── */}
      <Card className="p-3.5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Search Input */}
          <div className="relative w-full sm:w-80">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-surface-400" />
            <input
              type="text"
              className="input pl-9 pr-8 text-sm w-full"
              placeholder="Search by category name or slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 text-xs font-bold"
              >
                ✕
              </button>
            )}
          </div>

          {/* Action Toolbar */}
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end flex-wrap">
            {/* Tree View Controls */}
            {viewMode === 'tree' && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={ArrowsPointingOutIcon}
                  onClick={expandAll}
                  className="text-xs h-8"
                  title="Expand all subcategories"
                >
                  Expand All
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={ArrowsPointingInIcon}
                  onClick={collapseAll}
                  className="text-xs h-8"
                  title="Collapse to root categories"
                >
                  Collapse All
                </Button>
              </>
            )}

            {/* View Mode Switcher */}
            <div className="inline-flex rounded-lg border border-surface-200 p-0.5 bg-surface-50">
              <button
                type="button"
                onClick={() => setViewMode('tree')}
                className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  viewMode === 'tree'
                    ? 'bg-white text-surface-900 shadow-sm'
                    : 'text-surface-500 hover:text-surface-900'
                }`}
              >
                <ListBulletIcon className="h-4 w-4" />
                <span>Tree View</span>
              </button>

              <button
                type="button"
                onClick={() => setViewMode('drilldown')}
                className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  viewMode === 'drilldown'
                    ? 'bg-white text-surface-900 shadow-sm'
                    : 'text-surface-500 hover:text-surface-900'
                }`}
              >
                <Squares2X2Icon className="h-4 w-4" />
                <span>Column Drilldown</span>
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* ── Main Content Area ── */}
      {loading ? (
        <Card>
          <LoadingPage />
        </Card>
      ) : categories.length === 0 ? (
        <Card>
          <EmptyState
            icon={TagIcon}
            title="No categories created yet"
            description="Start building your catalog structure by adding top-level root categories and nesting subcategories."
            action={
              <Button variant="primary" size="md" icon={PlusIcon} onClick={openCreateRootModal}>
                Add First Root Category
              </Button>
            }
          />
        </Card>
      ) : viewMode === 'drilldown' ? (
        <CategoryDrilldownExplorer
          categories={categories}
          onEdit={openEditModal}
          onAddSub={openAddSubModal}
          onToggleStatus={toggleStatus}
          onDelete={(c) => setDeleteConfirmCat(c)}
        />
      ) : (
        /* Expandable Hierarchical Tree Table */
        <Card className="overflow-hidden p-0 border-surface-200">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-surface-200 bg-surface-50/80 text-[11px] font-bold uppercase tracking-wider text-surface-500">
                  <th className="py-3 px-4 min-w-[280px]">Category Hierarchy (Click row or ▶ to expand)</th>
                  <th className="py-3 px-3">Slug</th>
                  <th className="py-3 px-3">Parent</th>
                  <th className="py-3 px-3">Gender</th>
                  <th className="py-3 px-3">Sort</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100 text-sm">
                {roots.map((root) => (
                  <CategoryTreeNode
                    key={root.id}
                    node={root}
                    level={0}
                    expandedIds={expandedIds}
                    toggleExpand={toggleExpand}
                    onEdit={openEditModal}
                    onAddSub={openAddSubModal}
                    onToggleStatus={toggleStatus}
                    onDelete={(c) => setDeleteConfirmCat(c)}
                    allCategoriesMap={categoryMap}
                    searchQuery={searchQuery}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Category Create / Edit Modal ── */}
      <CategoryModal
        open={showModal || !!editCat}
        cat={editCat}
        defaultParentId={defaultParentId}
        categories={categories}
        onClose={closeModal}
        onSaved={onSaved}
        business={business}
      />

      {/* ── Delete Confirmation Modal ── */}
      {deleteConfirmCat && (
        <Modal
          open={!!deleteConfirmCat}
          onClose={() => setDeleteConfirmCat(null)}
          title="Delete Category"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-sm text-surface-700">
              Are you sure you want to delete category{' '}
              <strong className="text-surface-900">"{deleteConfirmCat.name}"</strong>?
            </p>
            {deleteConfirmCat.children_count > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                ⚠️ <strong>Warning:</strong> This category contains {deleteConfirmCat.children_count}{' '}
                child subcategories. Deleting it may detach or delete its subcategories.
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-surface-100">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDeleteConfirmCat(null)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={TrashIcon}
                loading={deleting}
                onClick={() => deleteCat(deleteConfirmCat.id)}
              >
                Delete Permanently
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
